import { useEffect, useRef } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Avatar } from '@components/avatar';
import { Columns, DataGrid } from '@components/data-grid';
import { FilterQueryData, FiltersConjunctionType } from '@components/filters';
import { IconMessages } from '@components/icons/messages';
import { IconNotes } from '@components/icons/notes';
import { IconRatingGood } from '@components/icons/rating-good';
import { IconRatingMedium } from '@components/icons/rating-medium';
import { Pagination } from '@components/pagination';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { abbreviateDate } from '@utils/date';

import { ApplicationStatusIndicator } from 'components/application-status-indicator';
import { ApplicationLabels } from 'components/ApplicationLabels';
import { EmptyApplicationResult } from 'components/EmptyApplicationResult';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { usePermissions } from 'features/permissions';
import { getAvatarColor } from 'helpers/application-status';
import { getIconForRating } from 'helpers/reviews';
import { useApplicationOptions } from 'hooks/use-application-options';
import { useStore } from 'models/Provider';
import type { IApplication, IJob } from 'models/types';

type Props = {
  job: IJob;
  queryData: FilterQueryData;
  conjunction: FiltersConjunctionType;
};

export const Applicants = observer((props: Props) => {
  const { job, queryData, conjunction } = props;

  const actionsForApplication = useApplicationOptions();
  const { hasRolePermission } = usePermissions();
  const columns = useJobApplicantsColumns();

  const {
    applications,
    clearSelected,
    clickedSelectAll,
    hasSelectedAny,
    isFetching,
    load,
    numPages,
    page,
    reset,
    selected,
    selectedAllMatching,
    selectedAllOnPage,
    selectedCount,
    setPage,
    setSort,
    sort,
    toggleSelect,
    toggleSelectAll,
    totalCount,
  } = job.listView;

  const isFiltering = Object.keys(queryData).length > 0;

  useEffect(() => {
    job.setSearchQueryData(queryData, conjunction);
    job.loadJobApplicationsCount();
    load();
  }, [job, load, queryData, conjunction]);

  useEffect(() => {
    return reset;
  }, [reset]);

  if (applications.state !== 'loaded' && applications.results.length === 0) {
    return <Loading />;
  }

  if (applications.results.length === 0) {
    return (
      <EmptyApplicationResult
        isFiltered={isFiltering}
        hasSearch={Boolean(job.settings.searchStr)}
      />
    );
  }

  return (
    <PageContent noPadding>
      <Stack space={6}>
        <ApplicantsContent>
          {isFetching && <Loading />}
          <div
            style={{ ...(isFetching && { opacity: isFetching ? 0.7 : 1, pointerEvents: 'none' }) }}
          >
            <DataGrid
              actions={actionsForApplication}
              label="Applicants list"
              columns={columns}
              sort={sort}
              onSortChange={setSort}
              columnSettings={job.settings.columns.toJSON()}
              onColumnSettingsChange={job.settings.setColumns}
              data={applications.results}
              selection={
                !hasRolePermission('applicants:use-bulk-actions')
                  ? undefined
                  : {
                      isGroupSelected: selectedAllOnPage ? true : hasSelectedAny ? 'mixed' : false,
                      onRowSelect: toggleSelect,
                      isRowSelected: (row) => selected.has(row.id),
                      onGroupSelect: toggleSelectAll,
                      overlay: (application) => {
                        return (
                          <Avatar
                            size="md"
                            src={application.picture}
                            name={application.name}
                            color={getAvatarColor(application)}
                          />
                        );
                      },
                      element: (() => {
                        if (numPages < 2) return null;

                        if (selectedAllOnPage && clickedSelectAll) {
                          return (
                            <SelectedAllElement
                              totalCount={totalCount}
                              onClearSelect={clearSelected}
                            />
                          );
                        }

                        if (!clickedSelectAll && selectedAllOnPage) {
                          return (
                            <SelectAllElement
                              totalCount={totalCount}
                              selectedCount={selectedCount}
                              onAllSelect={selectedAllMatching}
                            />
                          );
                        }
                        return null;
                      })(),
                    }
              }
            />
          </div>
        </ApplicantsContent>

        <div style={{ marginRight: '1.5rem', marginBottom: '5rem' }}>
          <Pagination justify="right" count={numPages} selected={page} onSelect={setPage} />
        </div>
      </Stack>
    </PageContent>
  );
});

const ApplicantsContent = styled.div({
  position: 'relative',
  '@media(max-width: 720px)': {
    overflow: 'auto',
  },
});

const SelectedAllElement = observer((props: { totalCount: number; onClearSelect(): void }) => {
  const { totalCount, onClearSelect } = props;
  const { t } = useStore();
  return (
    <Stack orientation="horizontal" space={2} align="center">
      <Text
        font="regular14"
        color="purple100"
        text={t('job.applicants.show.selected.all', totalCount)}
      />
      <Text font="regular14" color="purple100" text={<span>&bull;</span>} />
      <ActionText onClick={onClearSelect} font="regular14">
        {t('job.applicants.selection.clear')}
      </ActionText>
    </Stack>
  );
});

const SelectAllElement = observer(
  ({
    selectedCount,
    totalCount,
    onAllSelect,
  }: {
    selectedCount: number;
    totalCount: number;
    onAllSelect(): void;
  }) => {
    const { t } = useStore();
    return (
      <Stack orientation="horizontal" space={2} align="center">
        <Text
          font="regular14"
          color="purple100"
          text={t('job.applicants.show.selected', selectedCount)}
        />
        <Text font="regular14" color="purple100" text={<span>&bull;</span>} />
        <ActionText onClick={onAllSelect} font="regular14">
          {t('job.applicants.filtered.applicants', totalCount)}
        </ActionText>
      </Stack>
    );
  },
);

const RatingsCountColumn = observer(({ application }: { application: IApplication }) => (
  <ActionText.Link font="regular14" color="purple100" to={application.baseUrl + '/ratings'}>
    {application.numReviews > 0 ? application.numReviews : '-'}
  </ActionText.Link>
));

const NotesColumn = observer(({ application }: { application: IApplication }) => (
  <ActionText.Link font="regular14" color="purple100" to={application.baseUrl + '/notes'}>
    {application.numNotes > 0 ? application.numNotes : '-'}
  </ActionText.Link>
));

const MessagesColumn = observer(({ application }: { application: IApplication }) => (
  <ActionText.Link font="regular14" color="purple100" to={application.baseUrl + '/messages'}>
    {application.numMessages > 0 ? application.numMessages : '-'}
  </ActionText.Link>
));

const EmailColumn = observer(({ application }: { application: IApplication }) => (
  <ActionText.ExternalLink font="regular14" color="purple100" href={`mailto:${application.email}`}>
    {application.email}
  </ActionText.ExternalLink>
));
const NameColumn = observer(({ application }: { application: IApplication }) => {
  const { hasRolePermission } = usePermissions();

  return (
    <Stack orientation="horizontal" space={2} align="center">
      {!hasRolePermission('application:use-application-selection') && (
        <Avatar
          size="md"
          src={application.picture}
          name={application.name}
          color={getAvatarColor(application)}
        />
      )}
      <ActionText.Link font="regular14" color="purple100" to={application.detailUrl}>
        {application.name}
      </ActionText.Link>
    </Stack>
  );
});

function useJobApplicantsColumns() {
  const { t, lang } = useStore();

  const columns = useRef<Columns<IApplication>>([
    {
      key: 'name',
      label: t('job.applicants.label.name'),
      type: 'text-long',
      sortable: true,
      render: (application) => <NameColumn application={application} />,
    },
    {
      key: 'date',
      label: t('job.applicants.label.applied'),
      type: 'date',
      sortable: true,
      render: (application) => <span>{abbreviateDate(application.date, lang)}</span>,
    },
    {
      key: 'email',
      label: t('job.applicants.label.email'),
      type: 'text-long',
      sortable: true,
      render: (application) => <EmailColumn application={application} />,
    },
    {
      key: 'jobStageName',
      label: t('job.applicants.label.stage'),
      sortable: true,
      type: 'text-long',
    },
    {
      key: 'status',
      label: t('job.applicants.label.status'),
      sortable: true,
      render: (application) => <ApplicationStatusIndicator status={application.status} />,
    },
    {
      label: t('job.applicants.label.labels'),
      key: 'labels',
      render: (a) => <ApplicationLabels application={a} />,
    },
    {
      labelIcon: IconMessages,
      label: t('job.applicants.label.messages'),
      sortable: true,
      center: true,
      key: 'numMessages',
      type: 'numeric',
      render: (application) => <MessagesColumn application={application} />,
    },
    {
      labelIcon: IconNotes,
      label: t('job.applicants.label.notes'),
      sortable: true,
      center: true,
      key: 'numNotes',
      type: 'numeric',
      render: (application) => <NotesColumn application={application} />,
    },
    {
      labelIcon: IconRatingGood,
      label: t('job.applicants.label.reviews'),
      sortable: true,
      center: true,
      key: 'numReviews',
      type: 'numeric',
      render: (application) => <RatingsCountColumn application={application} />,
    },
    {
      labelIcon: IconRatingMedium,
      label: t('job.applicants.label.avgrating'),
      sortable: true,
      center: true,
      key: 'averageReviews',
      type: 'numeric',
      render: (application) => getIconForRating(application.averageReviews, true) || <span>-</span>,
    },
  ]);

  return columns.current;
}
